import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, message, Row, Col, Radio } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

const ForgotCredentials: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [resetType, setResetType] = useState<'password' | 'username'>('password');
  const [codeSent, setCodeSent] = useState(false);

  const onFinishStep1 = async (values: { email: string; resetType: string }) => {
    setLoading(true);
    try {
      if (values.resetType === 'password') {
        await authAPI.forgotPassword(values.email);
        setEmail(values.email);
        setCodeSent(true);
        setStep(1);
        message.success('Код восстановления отправлен на ваш email');
      } else {
        await authAPI.forgotUsername(values.email);
        setStep(2);
        message.success('Данные для входа отправлены на ваш email');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка отправки');
    } finally {
      setLoading(false);
    }
  };

  const onFinishStep2 = async (values: { code: string; newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('Пароли не совпадают');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(email, values.code, values.newPassword);
      setStep(2);
      message.success('Пароль успешно изменен');
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка сброса пароля');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      message.success('Код повторно отправлен на ваш email');
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка отправки кода');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f0f2f5',
      padding: '10px'
    }}>
      <Row justify="center" style={{ width: '100%' }}>
        <Col xs={22} sm={18} md={14} lg={10} xl={8}>
          <Card
            style={{
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              borderRadius: '8px'
            }}
            variant="outlined"
          >
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                🔐 Восстановление доступа
              </Title>
              <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Восстановите пароль или получите напоминание имени пользователя
              </Text>
            </div>


            {step === 0 && (
              <Form
                name="forgot-credentials"
                onFinish={onFinishStep1}
                autoComplete="off"
                size="middle"
                layout="vertical"
                initialValues={{ resetType: 'password' }}
              >
                <Form.Item
                  label="Что нужно восстановить?"
                  name="resetType"
                  rules={[{ required: true, message: 'Выберите тип восстановления' }]}
                >
                  <Radio.Group 
                    value={resetType} 
                    onChange={(e) => setResetType(e.target.value)}
                    style={{ 
                      width: '100%',
                      display: 'flex',
                      alignItems: 'stretch'
                    }}
                  >
                    <Radio.Button 
                      value="password" 
                      style={{ 
                        width: '50%', 
                        height: '60px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        lineHeight: '1.2'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                        <LockOutlined style={{ marginRight: '4px' }} />
                        <span>Пароль</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        Забыли пароль
                      </div>
                    </Radio.Button>
                    <Radio.Button 
                      value="username" 
                      style={{ 
                        width: '50%', 
                        height: '60px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        lineHeight: '1.2'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                        <UserOutlined style={{ marginRight: '4px' }} />
                        <span>Имя пользователя</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        Забыли логин
                      </div>
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Введите email' },
                    { type: 'email', message: 'Введите корректный email' }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder="your@email.com"
                    autoComplete="off"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: '8px' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{ width: '100%', height: '32px' }}
                  >
                    {resetType === 'password' ? 'Отправить код' : 'Отправить напоминание'}
                  </Button>
                </Form.Item>
              </Form>
            )}

            {step === 1 && resetType === 'password' && (
              <Form
                name="reset-password"
                onFinish={onFinishStep2}
                autoComplete="off"
                size="middle"
                layout="vertical"
              >
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '12px', 
                  backgroundColor: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: '6px'
                }}>
                  <Text>
                    Код восстановления отправлен на <strong>{email}</strong>
                  </Text>
                </div>

                <Form.Item
                  label="Код из email"
                  name="code"
                  rules={[
                    { required: true, message: 'Введите код' },
                    { pattern: /^\d{6}$/, message: 'Код должен содержать 6 цифр' }
                  ]}
                >
                  <Input
                    placeholder="123456"
                    maxLength={6}
                    style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '2px' }}
                  />
                </Form.Item>

                <Form.Item
                  label="Новый пароль"
                  name="newPassword"
                  rules={[
                    { required: true, message: 'Введите новый пароль' },
                    { min: 6, message: 'Минимум 6 символов' }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Новый пароль"
                  />
                </Form.Item>

                <Form.Item
                  label="Подтвердите пароль"
                  name="confirmPassword"
                  rules={[
                    { required: true, message: 'Подтвердите пароль' }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Повторите пароль"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: '8px' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{ width: '100%', height: '32px' }}
                  >
                    Сбросить пароль
                  </Button>
                </Form.Item>

                <div style={{ textAlign: 'center' }}>
                  <Button type="link" onClick={resendCode} loading={loading}>
                    Отправить код повторно
                  </Button>
                </div>
              </Form>
            )}

            {step === 2 && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <CheckCircleOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#52c41a', marginBottom: '8px' }}>
                  {resetType === 'password' ? 'Пароль успешно изменен!' : 'Данные для входа отправлены!'}
                </Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                  {resetType === 'password' 
                    ? 'Теперь вы можете войти в систему с новым паролем'
                    : 'Проверьте email - там будут имя пользователя и пароль'
                  }
                </Text>
                <Button type="primary" size="large">
                  <Link to="/">Войти в систему</Link>
                </Button>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <Text type="secondary">
                Вспомнили данные? <Link to="/">Войти</Link>
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ForgotCredentials;
